<template>
  <div
    class="matrix-grid-column"
    :class="{ answered }"
  >
    <div
      v-for="answer in answers"
      :key="`column-${answer.id}`"
      class="matrix-grid-choice-container"
    >
      <button
        class="choice-answer"
        :class="{ expanded: expandedChoices.includes(answer.id), selected: hasAnswerBeenAnswered(answer.id) }"
        @click="handleChoiceExpansion(answer.id)"
      >
        <div
          class="answer-indicator"
          :class="{ correct: isAnswerCorrect(answer.id), incorrect: isAnswerIncorrect(answer.id), sata: allowMultipleAnswers }"
        />
        <div class="answer-text">
          <p class="answer-header">
            {{ answer.text }}
          </p>
          <p
            v-if="hasAnswerBeenAnswered(answer.id)"
            class="selected-answers"
          >
            {{ selectedAnswerString(answer.id) }}
          </p>
        </div>
        <icon
          class="expansion-icon"
          name="arrow-down"
        />
      </button>
      <div
        v-for="(header, selectionIndex) in headers"
        :key="header"
        class="choice-header"
      >
        <matrix-grid-input
          :answer="answer"
          :answered="answered"
          :correct="isSelectionCorrect(answer.id, selectionIndex)"
          :incorrect="isSelectionIncorrect(answer.id, selectionIndex)"
          :input-id="'column'"
          :input-type="inputType"
          :selected="isSelectionSelected(answer.id, selectionIndex)"
          :selection-index="selectionIndex"
          @handle-answer="handleAnswer"
        >
          <p class="header-text">
            {{ header }}
          </p>
        </matrix-grid-input>
      </div>
      <div
        v-if="answered && answer.rationale"
        class="answer-rationale"
        :class="{ locked: rationalesLocked }"
        v-html="answer.rationale"
      />
    </div>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';
import MatrixGridInput from '@/components/flashcards/fragments/matrix-grid-input';

import {
  checkMatrixGridAnswerCorrectness,
  checkMatrixGridAnswerAnswered,
  checkMatrixGridSelectionSelected,
  checkMatrixGridSelectionCorrectness,
} from '@/utils/flashcards';

export default {
  name: 'MatrixGridColumn',
  components: { Icon, MatrixGridInput },
  props: {
    answers: {
      type: Array,
    },
    answered: {
      type: Boolean,
    },
    flashcard: {
      type: Object,
    },
    inputType: {
      type: String,
    },
    headers: {
      type: Array,
    },
    matrixGridSelections: {
      type: Array,
    },
    rationalesLocked: {
      type: Boolean,
    },
  },
  emits: ['handle-answer'],
  data() {
    return {
      expandedChoices: [],
    };
  },
  computed: {
    allowMultipleAnswers() {
      return this.inputType === 'checkbox';
    },
  },
  methods: {
    handleAnswer(params) {
      this.$emit('handle-answer', params);
    },
    handleChoiceExpansion(answerId) {
      if (this.expandedChoices.includes(answerId)) {
        this.expandedChoices = this.expandedChoices.filter(choice => choice !== answerId);
      } else {
        this.expandedChoices.push(answerId);
      }
    },
    hasAnswerBeenAnswered(answerId) {
      return checkMatrixGridAnswerAnswered(this.matrixGridSelections, answerId);
    },
    isAnswerCorrect(answerId) {
      return checkMatrixGridAnswerCorrectness(this.flashcard, this.matrixGridSelections, answerId);
    },
    isAnswerIncorrect(answerId) {
      return !this.isAnswerCorrect(answerId);
    },
    isSelectionCorrect(answerId, selectionIndex) {
      const correctness = checkMatrixGridSelectionCorrectness(this.flashcard, this.matrixGridSelections, answerId, selectionIndex);
      return correctness === 'correct';
    },
    isSelectionIncorrect(answerId, selectionIndex) {
      const correctness = checkMatrixGridSelectionCorrectness(this.flashcard, this.matrixGridSelections, answerId, selectionIndex);
      return correctness === 'incorrect';
    },
    isSelectionSelected(answerId, selectionIndex) {
      return checkMatrixGridSelectionSelected(this.matrixGridSelections, answerId, selectionIndex);
    },
    selectedAnswerString(answerId) {
      return this.matrixGridSelections
        ?.find(answerObj => answerObj.answerId === answerId)?.selections
        ?.map((val, index) => val && this.headers[index])
        .filter(val => val)
        .join(', ');
    },
  },
};
</script>
<style lang="scss">
$border-color: #d0d2d6;

.matrix-grid-column {
  @include breakpoint(small) {
    display: flex;
  }

  background-color: #f7f7f7;
  display: none;
  flex-direction: column;
  margin: 0px -16px;

  .matrix-grid-choice-container {
    background-color: #ffffff;
    border-top: 1px solid $border-color;
    border-bottom: 1px solid $border-color;
    margin-top: 8px;

    .choice-answer {
      border: none;
      background: transparent;
      align-items: center;
      display: flex;
      padding: 24px;
      width: 100%;

      .answer-indicator {
        border: 1px solid #00d8f8;
        border-radius: 50%;
        content: '';
        height: 8px;
        margin-right: 8px;
        width: 8px;

        &.sata {
          border-radius: 2px;
        }
      }

      .answer-text {
        text-align: left;

        .answer-header,
        .selected-answers {
          margin: 0;
        }

        .answer-header {
          font-size: rem-calc(16);
          font-weight: $bold;
        }

        .selected-answers {
          font-size: rem-calc(14);
          padding: 4px 0;
        }
      }

      .expansion-icon {
        height: 12px;
        margin-left: auto;
        transition: .15s linear all;
        width: 12px;
      }

      &.expanded {
        .expansion-icon {
          transform: rotate(180deg);
        }

        &~.choice-header,
        &~.answer-rationale {
          display: block;
        }
      }

      &.selected {
        background-color: #e5fbfe;

        .answer-indicator {
          background-color: #00d8f8;
        }
      }

      &~.choice-header {
        display: none;
      }
    }

    .choice-header {
      border-top: 1px solid $border-color;

      .answer-label {
        display: inline-flex;
        padding: 24px;
        width: 100%;

        .header-text {
          margin: 0;
          margin-left: 12px;
        }
      }
    }

    &:last-child {
      margin-bottom: 8px
    }
  }

  .answer-rationale {
    border-top: 1px solid $border-color;
    display: none;
    font-size: rem-calc(14);
    line-height: 1.7;
    padding: rem-calc(16);

    &.locked {
      filter: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg"><filter id="filter"><feGaussianBlur stdDeviation="5" /></filter></svg>#filter');
      filter: blur(4px);
    }
  }

  &.answered {
    .answer-indicator {
      &.correct {
        background-color: #00b866 !important;
        border-color: #00b866;
      }

      &.incorrect {
        background-color: #ee1300 !important;
        border-color: #ee1300;
      }
    }
  }
}
</style>
